import { Button } from '@bambi/component-library/src/components/Button/Button';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { type MouseEventHandler, useCallback } from 'react';
import { useNavigate } from 'react-router';

import type { NotificationMetaDataCallback } from './types';
import { type NotificationRead } from 'src/common/external/bambi-api/notificationsApi';
import { useTripsSubscriptionsRetrieveQuery } from 'src/common/external/bambi-api/tripsApi.generated';
import { formatPassengerFullName } from 'src/common/util/formatFullName';

export function TripSubscriptionExpiringSoonRenderer({
  notification,
}: {
  notification: NotificationRead;
}) {
  const { data: subscription, isLoading } = useTripsSubscriptionsRetrieveQuery(
    {
      id: notification.ref_entity_id || '',
    },
    { skip: !notification.ref_entity_id }
  );

  const navigate = useNavigate();

  const handleOpenTripSubscriptionPage = useCallback<MouseEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      navigate(`/trips/subscriptions/${notification.ref_entity_id}`);
    },
    [navigate, notification]
  );

  if (!notification.ref_entity_id) {
    return <div className="text-sm">Subscription not found</div>;
  }

  if (isLoading) {
    return <LoadingIndicator variant="small" />;
  }

  if (!subscription) {
    return <div className="text-sm">Subscription not found</div>;
  }

  return (
    <div className="flex flex-col text-sm">
      <div>
        <strong>Who: </strong>
        {formatPassengerFullName(subscription.passenger)}
      </div>
      <div>
        <strong>Expires At: </strong>
        {subscription.expires_at}
      </div>
      <Button
        variant="anchor-inline"
        justifyContent="start"
        onClick={handleOpenTripSubscriptionPage}
      >
        View on Trips &gt; Subscriptions
      </Button>
    </div>
  );
}

function TripSubscriptionExpiringSoonTitle({
  notification,
}: {
  notification: NotificationRead;
}) {
  const { data, isLoading } = useTripsSubscriptionsRetrieveQuery(
    {
      id: notification.ref_entity_id || '',
    },
    { skip: !notification.ref_entity_id }
  );

  if (!notification.ref_entity_id) {
    return 'Trip subscription expiring';
  }

  if (isLoading) {
    return <LoadingIndicator variant="small" />;
  }

  if (!data) {
    return 'Unknown trip subscription expiring';
  }

  return `Trip Subscription for ${formatPassengerFullName(data.passenger)} Expiring Soon`;
}

export const getMetaData: NotificationMetaDataCallback = (notification) => {
  return {
    title: <TripSubscriptionExpiringSoonTitle notification={notification} />,
  };
};
